/**
 * Safely sanitize a user-provided redirect URL.
 * - Allows only same-site relative paths.
 * - Blocks absolute URLs to other domains.
 * - Blocks protocol-based injection like "javascript:".
 * - Falls back to a safe default path ("/") when unsafe.
 */
export function sanitizeRedirectParam(next: string | undefined | null, fallback: string = '/'): string {
  if (!next || typeof next !== 'string') {
    return fallback;
  }

  try {
    // Trim whitespace
    const value = next.trim();

    // Disallow JS protocol or anything not starting with "/" or "./" or "../"
    if (/^\s*(javascript:|data:|vbscript:)/i.test(value)) {
      return fallback;
    }

    // If it's an absolute URL, ensure the same origin
    // But redirects should usually NOT allow absolute URLs at all
    try {
      const url = new URL(value, window.location.origin);

      // Ensure it resolves to same origin AND it started as a relative path
      // (To avoid sneaky absolute URLs like http://evil.com/ being allowed)
      if (url.origin === window.location.origin && value.startsWith('/')) {
        return url.pathname + url.search + url.hash;
      } else {
        return fallback;
      }
    } catch {
      // If URL constructor fails, it's not a valid URL — treat as unsafe
      return fallback;
    }
  } catch {
    return fallback;
  }
}
